// src/services/analyticsService.js
import { 
  collection, 
  addDoc, 
  getDocs,
  query, 
  where, 
  orderBy,
  limit,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

const ANALYTICS_COLLECTION = 'docs-document-analytics';

export const analyticsService = {
  // Track document view
  async trackDocumentView(documentId, userId, documentName, sessionId = null) {
    try {
      await addDoc(collection(db, ANALYTICS_COLLECTION), {
        documentId,
        userId,
        documentName,
        sessionId: sessionId || this.generateSessionId(),
        action: 'view',
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent,
        platform: this.getPlatform(),
        referrer: document.referrer || 'direct'
      });
    } catch (error) {
      console.error('Error tracking document view:', error);
    }
  },

  // Track document download
  async trackDocumentDownload(documentId, userId, documentName) {
    try {
      await addDoc(collection(db, ANALYTICS_COLLECTION), {
        documentId,
        userId,
        documentName,
        action: 'download',
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent,
        platform: this.getPlatform()
      });
    } catch (error) {
      console.error('Error tracking document download:', error);
    }
  },

  // Get document analytics
  async getDocumentAnalytics(documentId, days = 30) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      
      const q = query(
        collection(db, ANALYTICS_COLLECTION),
        where('documentId', '==', documentId),
        where('timestamp', '>=', cutoffDate),
        orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching document analytics:', error);
      return [];
    }
  },

  // Get user's recent document views
  async getUserRecentViews(userId, limitCount = 10) {
    try {
      const q = query(
        collection(db, ANALYTICS_COLLECTION),
        where('userId', '==', userId),
        where('action', '==', 'view'),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching user recent views:', error);
      return [];
    }
  },

  // Get popular documents
  async getPopularDocuments(days = 7, limitCount = 10) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      
      const q = query(
        collection(db, ANALYTICS_COLLECTION),
        where('action', '==', 'view'),
        where('timestamp', '>=', cutoffDate),
        orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const views = querySnapshot.docs.map(doc => doc.data());
      
      // Group by documentId and count views
      const documentCounts = views.reduce((acc, view) => {
        acc[view.documentId] = (acc[view.documentId] || 0) + 1;
        return acc;
      }, {});
      
      // Sort by view count and return top documents
      return Object.entries(documentCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, limitCount)
        .map(([documentId, viewCount]) => ({
          documentId,
          viewCount,
          documentName: views.find(v => v.documentId === documentId)?.documentName
        }));
    } catch (error) {
      console.error('Error fetching popular documents:', error);
      return [];
    }
  },

  // Helper methods
  generateSessionId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  },

  getPlatform() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Mobile')) return 'mobile';
    if (userAgent.includes('Tablet')) return 'tablet';
    return 'desktop';
  }
};